import { NativeModules } from "react-native";
import { parseISO } from "date-fns";
import { os } from "../platform";

/**
 * Converts a date string to an ISO 8601 string in UTC timezone.
 *
 * @param {string} dateString - The date string to format.
 * @param {number|null} time - The time in HHMM format (optional).
 * @returns {string} The formatted ISO 8601 date string in UTC.
 */
const formatDateWithTimezone = (dateString, time) => {
  if (time) {
    const hours = time.toString().padStart(4, "0").slice(0, 2);
    const minutes = time.toString().padStart(4, "0").slice(2, 4);
    return parseISO(`${dateString}T${hours}:${minutes}:00`).toISOString();
  }
  return parseISO(dateString).toISOString();
};

/** Number of milliseconds in a single day (24 hours). */
const MS_PER_DAY = 86400000;

/**
 * Checks if two dates fall on the same UTC day.
 *
 * @param {Date|string|number} a - The first date, can be a Date object, string, or timestamp.
 * @param {Date|string|number} b - The second date, can be a Date object, string, or timestamp.
 * @returns {boolean} True if both dates are on the same UTC day, false otherwise.
 */
const isSameDay = (a, b) => {
  const d1 = new Date(a);
  const d2 = new Date(b);

  return (
    d1.getUTCFullYear() === d2.getUTCFullYear() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCDate() === d2.getUTCDate()
  );
};

/**
 * Formats a date into an ICS-compatible UTC datetime string (e.g., "20250101T120000Z").
 *
 * @param {Date|string|number} date - The date to format.
 * @returns {string} The formatted ICS datetime string in UTC.
 */
const formatICSDateTime = (date) => {
  const d = new Date(date);

  return (
    d.getUTCFullYear().toString() +
    String(d.getUTCMonth() + 1).padStart(2, "0") +
    String(d.getUTCDate()).padStart(2, "0") +
    "T" +
    String(d.getUTCHours()).padStart(2, "0") +
    String(d.getUTCMinutes()).padStart(2, "0") +
    String(d.getUTCSeconds()).padStart(2, "0") +
    "Z"
  );
};

/**
 * Adds an event to the user's calendar based on the platform (iOS, Android, or Web).
 *
 * @async
 * @function
 * @param {string} name - The name/title of the event.
 * @param {Date|string} start - The start date and time of the event, YYYY-MM-DD format.
 * @param {Date|string} end - The end date and time of the event, YYYY-MM-DD format.
 * @param {number|null} startTime - The start time of the event in HHMM format (optional).
 * @param {number|null} endTime - The end time of the event in HHMM format (optional).
 * @param {string} address - The location/address of the event.
 * @param {string} [description=""] - A description of the event (optional).
 * @param {string} [url=""] - A URL associated with the event (optional).
 * @param {Object} [repeatRule={ frequency: "DAILY|WEEKLY|MONTHLY|YEARLY", interval: 1 }] - The repeat rule for the event (optional).
 * @returns {Promise<void>} Resolves when the event has been added to the calendar.
 */
export const addToCalendar = async ({
  name,
  start,
  end,
  startTime = null,
  endTime = null,
  address,
  description = "",
  url = "",
  repeatRule = null,
}) => {
  try {
    const event = {
      name,
      start: formatDateWithTimezone(start, startTime),
      end: formatDateWithTimezone(end, endTime),
      address,
      description,
      url,
      repeatRule,
    };

    switch (os) {
      case "ios":
        await createCalendarEventiOS(event);
        break;
      case "android":
        await createCalendarEventAndroid(event);
        break;
      default:
        createCalendarEventWeb(event);
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Creates a calendar event on iOS devices using the native CalendarModule.
 *
 * @async
 * @function createCalendarEventiOS
 * @param {Object} event - The event details.
 * @param {string} event.name - The name/title of the event.
 * @param {string|number} event.start - The start time of the event (format depends on native module).
 * @param {string|number} event.end - The end time of the event (format depends on native module).
 * @param {string} event.address - The address/location of the event.
 * @param {string} event.description - The description of the event.
 * @param {string} event.url - The URL associated with the event.
 * @param {Object} [repeatRule={ frequency: "DAILY|WEEKLY|MONTHLY|YEARLY", interval: 1 }] - The repeat rule for the event (optional).
 * @returns {Promise<void>} Resolves when the event is added or logs an error if permission is not given.
 */
const createCalendarEventiOS = async (event) => {
  const { CalendarModule } = NativeModules;

  const repeatRule = event.repeatRule;

  let repeatFrequency = null;
  let repeatInterval = null;
  let repeatEndDate = null;

  if (repeatRule) {
    repeatFrequency = repeatRule.frequency;
    repeatInterval = repeatRule.interval;

    const end = new Date(event.start);
    end.setFullYear(end.getFullYear() + 1);
    repeatEndDate = end.toISOString();
  }

  try {
    await CalendarModule.addEvent(
      event.name,
      event.start,
      event.end,
      event.address,
      event.description,
      event.url,
      repeatFrequency,
      repeatInterval,
      repeatEndDate
    );
  } catch (error) {
    throw error;
  }
};

/**
 * Creates a calendar event on Android devices using the native CalendarModule.
 *
 * @param {Object} event - The event details.
 * @param {string} event.name - The name/title of the event.
 * @param {string} event.description - The description of the event.
 * @param {string} event.address - The location/address of the event.
 * @param {string|Date} event.start - The start date and time of the event.
 * @param {string|Date} event.end - The end date and time of the event.
 * @param {string} event.repeatRule.frequency - The frequency of the repeat rule (e.g., "DAILY", "WEEKLY").
 * @param {number} event.repeatRule.interval - The interval for the repeat rule (e.g., 1 for every day, 2 for every other day).
 */
const createCalendarEventAndroid = async (event) => {
  try {
    const startMillis = new Date(event.start).getTime();
    const endMillis = new Date(event.end).getTime();
    const allDay = startMillis === endMillis;

    const repeatEndDate = new Date(event.start);
    repeatEndDate.setFullYear(repeatEndDate.getFullYear() + 1);
    const repeatEndMillis = repeatEndDate.getTime();

    await NativeModules.CalendarModule.addEvent(
      event.name,
      event.description,
      event.address,
      startMillis,
      endMillis,
      allDay,
      event.repeatRule !== null ? `FREQ=${event.repeatRule.frequency};INTERVAL=${event.repeatRule.interval}` : null,
      event.repeatRule !== null ? repeatEndMillis : null
    );
  } catch (error) {
    throw error;
  }
};

/**
 * Generates and triggers a download of an ICS (iCalendar) file for the given event.
 *
 * @param {Object} event - The event details.
 * @param {string|Date} event.start - The start date/time of the event.
 * @param {string|Date} event.end - The end date/time of the event.
 * @param {string} event.name - The name or summary of the event.
 * @param {string} event.description - The HTML description of the event.
 * @param {string} event.address - The location/address of the event.
 * @param {Object} [event.repeatRule] - The repeat rule for the event (optional).
 * @param {string} event.repeatRule.frequency - The frequency of the repeat rule (e.g., "DAILY", "WEEKLY").
 * @param {number} event.repeatRule.interval - The interval for the repeat rule (e.g., 1 for every day, 2 for every other day).
 *
 * @returns {void}
 */
const createCalendarEventWeb = (event) => {
  try {
    const isAllDay = isSameDay(event.start, event.end);

    const formatICSDateOnly = (date) => {
      const d = new Date(date);
      return (
        d.getUTCFullYear().toString() +
        String(d.getUTCMonth() + 1).padStart(2, "0") +
        String(d.getUTCDate()).padStart(2, "0")
      );
    };

    const generateICS = () => {
      const startDate = new Date(event.start);
      const endDate = new Date(event.end);

      const lines = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "CALSCALE:GREGORIAN",
        "METHOD:PUBLISH",
        "PRODID:-//VBConnect//Event//EN",
        "BEGIN:VEVENT",
        `UID:${Date.now()}@vbconnect.app`,
        `DTSTAMP:${formatICSDateTime(new Date())}`,

        `DTSTART:${formatICSDateTime(startDate)}`,
        `DTEND:${formatICSDateTime(endDate)}`,

        ...(event.repeatRule
          ? [
              `RRULE:FREQ=${event.repeatRule.frequency.toUpperCase()};INTERVAL=${
                event.repeatRule.interval || 1
              };UNTIL=${formatICSDateTime(new Date(startDate.setFullYear(startDate.getFullYear() + 1)))}`,
            ]
          : []),

        `SUMMARY:${event.name}`,
        `DESCRIPTION:${event.description}`,
        `LOCATION:${event.address || ""}`,

        "STATUS:CONFIRMED",
        "TRANSP:OPAQUE",

        "END:VEVENT",
        "END:VCALENDAR",
      ];

      if (isAllDay) {
        lines.push(
          `DTSTART;VALUE=DATE:${formatICSDateOnly(startDate)}`,
          `DTEND;VALUE=DATE:${formatICSDateOnly(new Date(startDate.getTime() + MS_PER_DAY))}`
        );
      } else {
        lines.push(`DTSTART:${formatICSDateTime(startDate)}`, `DTEND:${formatICSDateTime(endDate)}`);
      }

      if (event.repeatRule) {
        const repeatEndDate = new Date(startDate);
        repeatEndDate.setFullYear(repeatEndDate.getFullYear() + 1);

        const freq = event.repeatRule.frequency?.toUpperCase();
        const interval = event.repeatRule.interval || 1;
        const until = formatICSDateTime(repeatEndDate);

        lines.push(`RRULE:FREQ=${freq};INTERVAL=${interval};UNTIL=${until}`);
      }

      lines.push(
        `SUMMARY:${event.name}`,
        `DESCRIPTION:${event.description}`,
        `LOCATION:${event.address}`,
        "END:VEVENT",
        "END:VCALENDAR"
      );

      return lines.join("\r\n");
    };

    const downloadICS = () => {
      const icsContent = generateICS();
      const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
      const link = document.createElement("a");

      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download = `${event.name.replace(/[^a-z0-9]/gi, "_")}.ics`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    };

    downloadICS();
  } catch (error) {
    throw error;
  }
};
